import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  User,
  Globe,
  Lock,
  Sparkles,
  Loader2,
  LogOut,
  ChevronRight,
  Bell,
  Languages,
  Trash2,
  MapPin,
  Check,
  Clock,
  Crown,
  CreditCard,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const REGIONS = [
  { code: 'north_america', name: 'North America' },
  { code: 'europe', name: 'Europe' },
  { code: 'latam', name: 'Latin America' },
  { code: 'apac', name: 'Asia-Pacific' },
  { code: 'africa', name: 'Africa' },
  { code: 'middle_east', name: 'Middle East' },
  { code: 'south_asia', name: 'South Asia' },
  { code: 'southeast_asia', name: 'Southeast Asia' }
];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'pt', name: 'Português' },
  { code: 'de', name: 'Deutsch' },
  { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' }
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout, fetchUser, updateLanguage } = useAuth();
  const [upgrading, setUpgrading] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [region, setRegion] = useState(user?.region || 'north_america');
  const [language, setLanguage] = useState(user?.preferred_language || 'en');
  const [savingSettings, setSavingSettings] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setRegion(user.region || 'north_america');
      setLanguage(user.preferred_language || 'en');
      setNotifications(user.notifications_enabled !== false);
      setAutoTranslate(user.auto_translate !== false);
    }
  }, [user]);

  const handleSaveSettings = async (settingKey, value) => {
    setSavingSettings(true);
    try {
      const settings = { [settingKey]: value };
      await api.updateUserSettings(settings);
      await fetchUser();
      toast.success(t('settings.changesSaved'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLanguageChange = async (value) => {
    setLanguage(value);
    // Use the centralized language update function
    // This updates UI, saves to backend, and triggers data refetch across the app
    await updateLanguage(value);
  };

  const handleUpgrade = async (tier = 'basic') => {
    setUpgrading(true);
    try {
      const lookupKey = tier === 'pro' ? 'pro_monthly' : 'basic_monthly';
      const response = await api.createCheckout(lookupKey, window.location.origin);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setUpgrading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const response = await api.createCustomerPortal();
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      toast.error('Unable to open billing portal. Please try again.');
      setOpeningPortal(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount();
      toast.success(t('settings.accountDeleted'));
      logout();
      navigate('/');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error.response?.data?.detail || t('settings.deleteAccountError'));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-bold text-foreground">{t('settings.title')}</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Trial/Subscription Status Banner - Most Visible */}
        {(user?.tier === 'free' || user?.tier === 'trial') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className={`rounded-2xl border-2 shadow-lg overflow-hidden ${
              user?.tier === 'trial' && user?.trial_status?.days_remaining <= 3 
                ? 'border-coral bg-coral/5' 
                : 'border-ocean-blue bg-ocean-blue/5'
            }`} data-testid="trial-status-banner">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    user?.tier === 'trial' ? 'bg-mint' : 'gradient-ocean'
                  }`}>
                    {user?.tier === 'trial' ? (
                      <Clock className="w-7 h-7 text-ocean-blue" />
                    ) : (
                      <Sparkles className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    {user?.tier === 'trial' ? (
                      <>
                        <h3 className="text-lg font-bold text-foreground mb-1">
                          {user?.trial_status?.days_remaining <= 3 ? (
                            <span className="text-coral">⚠️ Trial Ending Soon!</span>
                          ) : (
                            'Trial Active'
                          )}
                        </h3>
                        <p className="text-muted-foreground mb-3">
                          You have <span className="font-bold text-ocean-blue">{user?.trial_status?.days_remaining || 0} days</span> left in your free trial. 
                          Upgrade now to keep full access to all modules and features.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-bold text-foreground mb-1">
                          You're on the Free Plan
                        </h3>
                        <p className="text-muted-foreground mb-3">
                          Unlock all 650+ lessons, 35 labs, and regional rooms with a paid plan.
                        </p>
                      </>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={handleUpgrade}
                        disabled={upgrading}
                        className="rounded-xl gradient-ocean text-white"
                        data-testid="banner-upgrade-btn"
                      >
                        {upgrading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('settings.upgrading')}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Upgrade to Basic - $29/mo
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        className="rounded-xl border-coral text-coral hover:bg-coral/10"
                        onClick={() => handleUpgrade('pro')}
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Go Pro - $99/mo
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="rounded-2xl border-0 shadow-lg" data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-ocean-blue" />
                {t('common.profile')}
              </CardTitle>
              <CardDescription>{t('settings.accountInfo')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl gradient-ocean flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{user?.name}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <Separator />
              <div className="grid gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('settings.region')}</span>
                  <span className="font-medium capitalize">{user?.region?.replace(/_/g, ' ') || t('settings.notSet')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('settings.experience')}</span>
                  <span className="font-medium capitalize">{user?.experience_level?.replace(/_/g, ' ') || t('settings.notSet')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('settings.language')}</span>
                  <span className="font-medium">{LANGUAGES.find(l => l.code === user?.preferred_language)?.name || 'English'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Subscription Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className={`rounded-2xl border-0 shadow-lg ${
            user?.tier === 'basic' ? 'bg-gradient-to-br from-ocean-blue/5 to-sky-blue/5' : ''
          }`} data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-ocean-blue" />
                {t('settings.subscription')}
              </CardTitle>
              <CardDescription>{t('settings.currentPlan')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-foreground capitalize">{t(`tiers.${user?.tier}`)} {t('settings.tier')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {user?.tier === 'free' 
                      ? t('settings.freeAccess')
                      : t('settings.fullAccess')}
                  </p>
                </div>
                <Badge variant={user?.tier === 'basic' ? 'default' : 'secondary'} className={
                  user?.tier === 'basic' ? 'bg-ocean-blue' : ''
                }>
                  {user?.tier === 'basic' ? t('settings.active') : t('tiers.free')}
                </Badge>
              </div>
              
              {user?.tier === 'free' && (
                <>
                  <Separator />
                  <div className="p-4 rounded-xl bg-gradient-to-r from-ocean-blue/5 to-sky-blue/5 border border-ocean-blue/20">
                    <h4 className="font-semibold text-foreground mb-2">{t('settings.upgradeToBasic')}</h4>
                    <ul className="text-sm text-muted-foreground space-y-2 mb-4">
                      <li className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-ocean-blue" />
                        {t('settings.allModules')}
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-ocean-blue" />
                        {t('settings.allLabs')}
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-ocean-blue" />
                        {t('settings.regionalAccess')}
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-ocean-blue" />
                        {t('settings.weeklyPlans')}
                      </li>
                    </ul>
                    <Button 
                      onClick={handleUpgrade}
                      disabled={upgrading}
                      className="w-full rounded-xl gradient-ocean text-white"
                      data-testid="upgrade-btn"
                    >
                      {upgrading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('settings.upgrading')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t('settings.upgradeNow')}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Preferences Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="rounded-2xl border-0 shadow-lg" data-testid="preferences-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-ocean-blue" />
                {t('settings.preferences')}
              </CardTitle>
              <CardDescription>{t('settings.customizeExperience')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Region Dropdown */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-medium">{t('settings.region')}</Label>
                    <p className="text-xs text-muted-foreground">{t('settings.regionDesc')}</p>
                  </div>
                </div>
                <Select 
                  value={region} 
                  onValueChange={(value) => {
                    setRegion(value);
                    handleSaveSettings('region', value);
                  }}
                  disabled={savingSettings}
                >
                  <SelectTrigger className="w-[180px]" data-testid="region-select">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Separator />
              
              {/* Language Dropdown */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Languages className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-medium">{t('settings.language')}</Label>
                    <p className="text-xs text-muted-foreground">{t('settings.customizeExperience')}</p>
                  </div>
                </div>
                <Select 
                  value={language} 
                  onValueChange={handleLanguageChange}
                  disabled={savingSettings}
                >
                  <SelectTrigger className="w-[180px]" data-testid="language-select">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Separator />
              
              {/* Notifications Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-medium">{t('settings.notifications')}</Label>
                    <p className="text-xs text-muted-foreground">{t('settings.notificationsDesc')}</p>
                  </div>
                </div>
                <Switch 
                  checked={notifications} 
                  onCheckedChange={(value) => {
                    setNotifications(value);
                    handleSaveSettings('notifications_enabled', value);
                  }}
                  data-testid="notifications-toggle"
                />
              </div>
              <Separator />
              
              {/* Auto-translate Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <Label className="font-medium">{t('settings.autoTranslate')}</Label>
                    <p className="text-xs text-muted-foreground">{t('settings.autoTranslateDesc')}</p>
                  </div>
                </div>
                <Switch 
                  checked={autoTranslate} 
                  onCheckedChange={(value) => {
                    setAutoTranslate(value);
                    handleSaveSettings('auto_translate', value);
                  }}
                  data-testid="translate-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="rounded-2xl border-0 shadow-lg" data-testid="account-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-ocean-blue" />
                {t('settings.account')}
              </CardTitle>
              <CardDescription>{t('settings.manageAccount')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Analytics Link */}
              <Button 
                variant="outline"
                className="w-full justify-start rounded-xl"
                onClick={() => navigate('/analytics')}
                data-testid="analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Learning Analytics
              </Button>
              
              {/* Manage Subscription - Only show if user has stripe_customer_id */}
              {user?.stripe_customer_id && (
                <Button 
                  variant="outline"
                  className="w-full justify-start rounded-xl"
                  onClick={handleOpenPortal}
                  disabled={openingPortal}
                  data-testid="manage-subscription-btn"
                >
                  {openingPortal ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                </Button>
              )}
              
              <Separator />
              
              <Button 
                variant="outline"
                className="w-full justify-start rounded-xl"
              >
                <Lock className="w-4 h-4 mr-2" />
                {t('settings.changePassword')}
              </Button>
              <Button 
                variant="outline"
                onClick={handleLogout}
                className="w-full justify-start rounded-xl text-coral hover:text-coral hover:bg-coral/10 border-coral/30"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('settings.signOut')}
              </Button>
              <Separator />
              <Button 
                variant="ghost"
                className="w-full justify-start rounded-xl text-muted-foreground hover:text-destructive"
                onClick={() => setShowDeleteModal(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('settings.deleteAccount')}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Delete Account Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('settings.deleteAccountTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.deleteAccountWarning')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-muted-foreground">{t('settings.deleteAccountInfo')}</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• {t('settings.deleteProgress')}</li>
                <li>• {t('settings.deleteBadges')}</li>
                <li>• {t('settings.deleteChatHistory')}</li>
                <li>• {t('settings.deleteSubscription')}</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('settings.confirmDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
